import React, { useState, useEffect, useRef } from 'react';
import { 
  Modal, Box, Typography, TextField, Select, 
  MenuItem, IconButton, CircularProgress 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';      
import RemoveIcon from '@mui/icons-material/Remove'; 
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from 'axios';
import './index.css';
import S3_ASSETS from './assets/assets';

const DOMAIN = process.env.REACT_APP_DOMAIN;

const MEMBERSHIP_BENEFITS = [
  { img: S3_ASSETS.WORKSHOP, label: 'Expert Workshops' },
  { img: S3_ASSETS.MENTORSHIP, label: 'Mentorship Support' },
  { img: S3_ASSETS.NETWORKING, label: 'Networking Events' },
  { img: S3_ASSETS.CERTIFICATE, label: 'Certifications' }
];

const PROCESS_STEPS = [
  { id: 1, title: "Apply For Membership", desc: "From the website apply for membership by filling all the details as asked" },
  { id: 2, title: "Get Accepted", desc: "Once our team evaluated your profile, they will send you an acceptance email" },
  { id: 3, title: "Complete Your Onboarding", desc: "After acceptance, you can complete the onboarding formalities to be a part." }
];

const FAQ_DATA = [
  {
    category: "Membership Basics",
    questions: [
      { q: "What does my MarkoKnow membership include?", a: "Your MarkoKnow General Membership gives you access to learning resources, selected courses or content, community discussions, platform updates, and opportunities to grow within the MarkoKnow ecosystem." },
      { q: "Is my membership free or paid?", a: "The General Membership is completely free. Premium Membership, if offered, is invite-only." },
      { q: "How long is my membership valid?", a: "Your General Membership does not expire as long as your account remains active." }
    ]
  },
  {
    category: "Account & Profile",
    questions: [
      { q: "How do I update my profile information?", a: "You can update your profile details by going to your Member Dashboard → Edit Profile." }
    ]
  }
];

const MarkoknowLandingPage = () => {
  // --- Refs for Autoscroll ---
  const stepsRef = useRef(null);

  // --- UI Animation States ---
  const [activeStep, setActiveStep] = useState(1);
  const [hoveredStep, setHoveredStep] = useState(null);
  const intervalRef = useRef(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // --- Membership Form States ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [designation, setDesignation] = useState("student");
  const [otherDesignation, setOtherDesignation] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contactNo, setContactNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [contactNoError, setContactNoError] = useState("");

  // --- Custom Result Popup State ---
  const [resultModal, setResultModal] = useState({ open: false, message: "", isError: false });

  const scrollToSteps = () => {
    stepsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Validation Logic
  useEffect(() => {
    if (!email) setEmailError("");
    else setEmailError(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email) ? "" : "Invalid email format");
  }, [email]);

  useEffect(() => {
    if (!contactNo) setContactNoError("");
    else setContactNoError(contactNo.length === 10 ? "" : "Must be 10 digits");
  }, [contactNo]);

  // Step Animation Logic
  useEffect(() => {
    const startAnimation = () => {
      intervalRef.current = setInterval(() => {
        setActiveStep(prev => (prev === PROCESS_STEPS.length ? 1 : prev + 1));
      }, 3000);
    };
    if (!hoveredStep) startAnimation();
    else clearInterval(intervalRef.current);
    return () => clearInterval(intervalRef.current);
  }, [hoveredStep]);

  const handleFaqToggle = (index) => setOpenFaqIndex(openFaqIndex === index ? null : index);

  const handleSubmit = async () => {
    if (!name || !email || !contactNo || (designation === "other" && !otherDesignation)) {
      setResultModal({ open: true, message: "All fields are required", isError: true });
      return;
    }
    if (emailError || contactNoError) return;

    setLoading(true);
    try {
      const res = await axios.post(`${DOMAIN}/new/membership`, {
        name, email, contactNo,
        designation: designation === "other" ? otherDesignation : designation,
      }, {
        headers: { Authorization: `Bearer ${window.localStorage.getItem("token")}` },
      });
      setIsModalOpen(false);
      setResultModal({ open: true, message: res.data.message || "Application Submitted!", isError: false });
      // Reset fields
      setName(""); setEmail(""); setContactNo(""); setDesignation("student"); setOtherDesignation("");
    } catch (err) {
      setResultModal({ open: true, message: "Server error. Please try again.", isError: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mk-root-wrapper">
      {/* 1. Hero */}
      <section className="mk-hero-container" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url(${S3_ASSETS.HERO})` }}>
        <div className="mk-hero-content">
          <h1 className="mk-hero-title">Join the Markoknow Membership</h1>
          <p className="mk-hero-subtitle">Learn, Network, and Grow Your Startup Journey</p>
          <div className="mk-button-group">
            <button className="mk-btn mk-btn-primary" onClick={() => setIsModalOpen(true)}>Join Now</button>
            <button className="mk-btn mk-btn-secondary" onClick={scrollToSteps}>Learn More</button>
          </div>
        </div>
      </section>

      {/* 2. Why Join (Icon Grid) */}
      <section className="mk-section-tight mk-white-bg">
        <div className="mk-container-wide">
          <h2 className="mk-section-title">Why Join Markoknow <br /> Startup Community?</h2>
          <div className="mk-benefit-grid">
            <div className="mk-benefit-card">
              <div className="mk-icon-container">
                <img src={S3_ASSETS.COMMUNITY} alt="Community" className="mk-card-icon" />
              </div>
              <h3 className="mk-card-title-purple">Community Engagement</h3>
              <p className="mk-card-desc">Connect with Founders & Innovators</p>
            </div>
            <div className="mk-benefit-card">
              <div className="mk-icon-container">
                <img src={S3_ASSETS.SKILL} alt="Skills" className="mk-card-icon" />
              </div>
              <h3 className="mk-card-title-purple">Skill Development</h3>
              <p className="mk-card-desc">Develop Important Skills that Help you Grow</p>
            </div>
            <div className="mk-benefit-card">
              <div className="mk-icon-container">
                <img src={S3_ASSETS.MENTOR} alt="Mentorship" className="mk-card-icon" />
              </div>
              <h3 className="mk-card-title-purple">Mentorship & Guidance</h3>
              <p className="mk-card-desc">Get required assistance from time to time</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Member Perks (Image Grid) */}
      <section className="mk-section-tight mk-white-bg">
        <div className="mk-container">
          <h2 className="mk-section-title mk-dark-blue">What you will get as a Member</h2>
          <div className="mk-benefits-image-grid">
            {MEMBERSHIP_BENEFITS.map((item, i) => (
              <div key={i} className="mk-image-card">
                <div className="mk-img-wrapper">
                  <img src={item.img} alt={item.label} className="mk-benefit-img" />
                </div>
                <h4 className="mk-label-text">{item.label}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Stats Section */}
      <section className="mk-section mk-white-bg">
        <div className="mk-container mk-stats-wrapper">
          <img src={S3_ASSETS.STATS} alt="Stats" className="mk-stats-small-img" />
        </div>
      </section>

      {/* 5. Process Section (Target for scroll) */}
      <section className="mk-section-tight mk-white-bg" ref={stepsRef}>
        <div className="mk-container">
          <h2 className="mk-section-title">Membership Process</h2>
          <div className="mk-process-steps-container">
            {PROCESS_STEPS.map((step, idx) => {
              const isActive = hoveredStep === step.id || (!hoveredStep && activeStep === step.id);
              return (
                <div 
                  key={step.id} 
                  className={`mk-process-wrapper ${isActive ? '' : 'dimmed'}`} 
                  onMouseEnter={() => setHoveredStep(step.id)} 
                  onMouseLeave={() => setHoveredStep(null)}
                >
                  <div className={`mk-process-circle ${isActive ? 'active-purple' : 'light-purple'}`}>{step.id}.</div>
                  <h4 className="mk-process-step-title">{step.title}</h4>
                  <div className={`mk-process-box ${isActive ? 'active-purple' : 'light-purple'}`}><p>{step.desc}</p></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. FAQ Section */}
      <section className="mk-faq-dark-bg">
        <div className="mk-faq-container">
          <h2 className="mk-faq-main-header">FAQs</h2>
          {FAQ_DATA.map((section, sIdx) => (
            <div key={sIdx} className="mk-faq-group">
              <h3 className="mk-faq-category-title">{section.category}</h3>
              <div className="mk-faq-list-box">
                {section.questions.map((item, qIdx) => {
                  const gIdx = `${sIdx}-${qIdx}`;
                  const isOpen = openFaqIndex === gIdx;
                  return (
                    <div key={qIdx} className={`mk-faq-item ${isOpen ? 'is-open' : ''}`}>
                      <button className="mk-faq-question-bar" onClick={() => handleFaqToggle(gIdx)}>
                        <span>{item.q}</span>
                        {isOpen ? <RemoveIcon /> : <AddIcon />}
                      </button>
                      <div className="mk-faq-answer-drawer">
                        <div className="mk-faq-answer-inner"><p>{item.a}</p></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- POPUP FORM MODAL --- */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box className="mk-modal-box">
          <div className="mk-modal-header">
            <Typography variant="h5" className="mk-card-title-purple">Membership Form</Typography>
            <IconButton onClick={() => setIsModalOpen(false)}><CloseIcon /></IconButton>
          </div>
          <div className="mk-form-body">
            <TextField label="Full Name" fullWidth variant="filled" className="mk-input-field" value={name} onChange={(e) => setName(e.target.value)} />
            <TextField label="Email ID" fullWidth variant="filled" className="mk-input-field" error={!!emailError} helperText={emailError} value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField label="Contact Number" fullWidth variant="filled" className="mk-input-field" error={!!contactNoError} helperText={contactNoError} value={contactNo} onChange={(e) => setContactNo(e.target.value)} />
            <Typography variant="caption" color="textSecondary">Select Designation</Typography>
            <Select value={designation} onChange={(e) => setDesignation(e.target.value)} fullWidth className="mk-input-field">
              <MenuItem value="student">Student</MenuItem>
              <MenuItem value="working_professional">Working Professional</MenuItem>
              <MenuItem value="founder">Founder</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            {designation === "other" && <TextField label="Specify" fullWidth variant="filled" className="mk-input-field" value={otherDesignation} onChange={(e) => setOtherDesignation(e.target.value)} />}
            <button className="mk-btn mk-btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={handleSubmit} disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Application"}
            </button>
          </div>
        </Box>
      </Modal>

      {/* --- CUSTOM RESULT MODAL --- */}
      <Modal open={resultModal.open} onClose={() => setResultModal({ ...resultModal, open: false })}>
        <Box className="mk-result-modal">
          {resultModal.isError ? <ErrorOutlineIcon className="res-icon err" /> : <CheckCircleOutlineIcon className="res-icon succ" />}
          <Typography variant="h6" fontWeight="700">{resultModal.isError ? "Oops!" : "Success!"}</Typography>
          <Typography variant="body1" align="center" sx={{ my: 2 }}>{resultModal.message}</Typography>
          <button className="mk-btn mk-btn-primary" onClick={() => setResultModal({ ...resultModal, open: false })}>Close</button>
        </Box>
      </Modal>
    </div>
  );
};

export default MarkoknowLandingPage;